import type { Dispatch } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import useDebounce from "@entrilia/shared/hooks/useDebounce.ts";
import type {
  ApiReportResponse,
  FailedMeasure,
  PublicTabularResponse,
  QueryLoggingItem,
  RunApiReportRequest,
  RunTabularReportRequest,
  TabularDataResponse,
} from "../../../../../../../../api/types/svTypes.ts";
import { useBiCommonDataContext } from "../../../../contexts/BiCommonDataContext.ts";
import { isApiReportResponse } from "../../../../utilities/isApiReportResponse.ts";
import type { ReducerAction } from "../storage/gridDataState.ts";
import { GridDataStateActionType } from "../storage/gridDataState.ts";

export default function useDataLoadingViewer(
  requestConfig: RunTabularReportRequest,
  dispatch: Dispatch<ReducerAction>,
  runReport: <
    TBody extends RunTabularReportRequest | RunApiReportRequest,
    TResponse extends TabularDataResponse | ApiReportResponse,
  >(
    dataSetId: string,
    body: TBody,
    done: (data: ApiResponse<TResponse>) => void,
    error: (reason: string | undefined) => void
  ) => CancellationToken,
  setResponse: ((response: PublicTabularResponse | undefined) => void) | undefined,
  updateDevTools?: (loggingItems: QueryLoggingItem[] | undefined) => void,
  setEtag?: (etag: string | undefined, cacheValid: boolean | undefined) => void
) {
  const { dataSetId } = useBiCommonDataContext();
  const [dataLoadingCancellationToken, setDataLoadingCancellationToken] = useState<CancellationToken | undefined>(
    undefined
  );
  const [failedMeasures, setFailedMeasures] = useState<FailedMeasure[]>([]);

  const requestGenerationRef = useRef(0);
  const dataLoadingCancellationTokenRef = useRef(dataLoadingCancellationToken);

  useEffect(() => {
    dataLoadingCancellationTokenRef.current = dataLoadingCancellationToken;
  }, [dataLoadingCancellationToken]);

  const cancelDataLoading = useCallback(() => {
    requestGenerationRef.current += 1;
    dataLoadingCancellationTokenRef.current?.cancel();
  }, []);

  const requestConfigRef = useRef(requestConfig);

  useEffect(() => {
    requestConfigRef.current = requestConfig;
  }, [requestConfig]);

  const loadData = () => {
    cancelDataLoading();
    const generation = requestGenerationRef.current;

    dispatch({
      type: GridDataStateActionType.UPDATE_STATE,
      payload: { error: undefined },
    });
    dispatch({ type: GridDataStateActionType.LOADING_STARTED });
    setEtag?.(undefined, undefined);

    const calcConfig = { ...requestConfigRef.current };

    setDataLoadingCancellationToken(
      runReport(
        dataSetId,
        calcConfig,
        (resp) => {
          if (generation !== requestGenerationRef.current) {
            return;
          }
          if (setResponse && resp.data && isApiReportResponse(resp.data)) {
            setResponse(resp.data.publicResponse);
          }
          dispatch({ type: GridDataStateActionType.LOADING_ENDED });
          if (resp.success) {
            if (resp.data.errorCode) {
              dispatch({
                type: GridDataStateActionType.UPDATE_STATE,
                payload: { error: resp.data.errorCode },
              });
            } else {
              dispatch({
                type: GridDataStateActionType.UPDATE_STATE,
                payload: {
                  rows: resp.data.data || [],
                  snapshot: resp.data.snapshot,
                  groupTotals: resp.data.groupTotals,
                  grandTotals: resp.data.grandTotals,
                  totalCount: resp.data.snapshot?.detailCount ?? resp.data.totalCount,
                  columns: resp.data.columns,
                },
              });
              updateDevTools?.(resp.data.loggingItems);
              setEtag?.(resp.data.etag, undefined);
              setFailedMeasures(resp.data.failedMeasures);
            }
          } else {
            dispatch({
              type: GridDataStateActionType.UPDATE_STATE,
              payload: {
                error: resp.error?.message || "An error occurred while calculating report data. Please try again.",
              },
            });
          }
          dispatch({ type: GridDataStateActionType.INITIALIZED });
        },
        (error) => {
          if (generation !== requestGenerationRef.current) {
            return;
          }
          dispatch({ type: GridDataStateActionType.LOADING_ENDED });
          if (error === "canceled") {
            dispatch({
              type: GridDataStateActionType.UPDATE_STATE,
              payload: { error: undefined },
            });
          } else {
            dispatch({
              type: GridDataStateActionType.UPDATE_STATE,
              payload: { error: error || "An error occurred while calculating report data. Please try again." },
            });
          }
          dispatch({ type: GridDataStateActionType.INITIALIZED });
        }
      )
    );
  };

  const doLoadData = useDebounce(loadData, 100);

  const handleInvalidConfiguration = () => {
    cancelDataLoading();
    setResponse?.(undefined);
  };

  useEffect(() => {
    return () => {
      requestGenerationRef.current += 1;
      dataLoadingCancellationTokenRef.current?.cancel();
    };
  }, []);

  const scheduleLoadData = () => {
    cancelDataLoading();
    dispatch({ type: GridDataStateActionType.LOADING_STARTED });
    dispatch({
      type: GridDataStateActionType.UPDATE_STATE,
      payload: { error: undefined },
    });
    doLoadData();
  };

  return { loadData: scheduleLoadData, cancelDataLoading, failedMeasures, handleInvalidConfiguration };
}

export type ViewerDataLoadingReturnType = ReturnType<typeof useDataLoadingViewer>;

type CancellationToken = {
  cancel: () => void;
};
